import type { FC } from 'react';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { PermissionsProvider } from './context/PermissionsContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { PermissionRoute } from './components/auth/PermissionRoute';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { AppLayout } from './components/layout/AppLayout';
import { HomePage } from './pages/HomePage';
import { HealthPage } from './pages/HealthPage';
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';
import { NotFoundPage } from './pages/NotFoundPage';
import { WebmailPage } from './pages/WebmailPage';
import { CampaignsPage } from './pages/CampaignsPage';
import { BillingPage } from './pages/BillingPage';
import { ApiKeysPage } from './pages/ApiKeysPage';
import { AdminPage } from './pages/AdminPage';
import { DeliverabilityPage } from './pages/DeliverabilityPage';
import { UamPage } from './pages/UamPage';
import { TemplatesPage } from './pages/TemplatesPage';
import { ContactsPage } from './pages/ContactsPage';
import { TeamPage } from './pages/TeamPage';

export const App: FC = () => {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Public Auth Routes */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />

            {/* Authenticated Application Shell */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <PermissionsProvider>
                    <AppLayout />
                  </PermissionsProvider>
                </ProtectedRoute>
              }
            >
              <Route
                index
                element={
                  <PermissionRoute route="/" screenName="Overview Dashboard">
                    <HomePage />
                  </PermissionRoute>
                }
              />
              <Route
                path="webmail"
                element={
                  <PermissionRoute route="/webmail" screenName="Webmail Client">
                    <WebmailPage />
                  </PermissionRoute>
                }
              />
              <Route
                path="campaigns"
                element={
                  <PermissionRoute route="/campaigns" screenName="Email Campaigns">
                    <CampaignsPage />
                  </PermissionRoute>
                }
              />
              <Route
                path="templates"
                element={
                  <PermissionRoute route="/templates" screenName="Email Templates">
                    <TemplatesPage />
                  </PermissionRoute>
                }
              />
              <Route
                path="contacts"
                element={
                  <PermissionRoute route="/contacts" screenName="Audience & Contacts">
                    <ContactsPage />
                  </PermissionRoute>
                }
              />
              <Route
                path="team"
                element={
                  <PermissionRoute route="/team" screenName="Team & Employees">
                    <TeamPage />
                  </PermissionRoute>
                }
              />
              <Route
                path="deliverability"
                element={
                  <PermissionRoute route="/deliverability" screenName="Deliverability & Warmup">
                    <DeliverabilityPage />
                  </PermissionRoute>
                }
              />
              <Route
                path="billing"
                element={
                  <PermissionRoute route="/billing" screenName="Billing & Quotas">
                    <BillingPage />
                  </PermissionRoute>
                }
              />
              <Route
                path="api-keys"
                element={
                  <PermissionRoute route="/api-keys" screenName="API Keys">
                    <ApiKeysPage />
                  </PermissionRoute>
                }
              />
              <Route
                path="uam"
                element={
                  <PermissionRoute route="/uam" screenName="Access Control (UAM)">
                    <UamPage />
                  </PermissionRoute>
                }
              />
              <Route
                path="admin"
                element={
                  <PermissionRoute route="/admin" screenName="Platform Admin">
                    <AdminPage />
                  </PermissionRoute>
                }
              />
              <Route
                path="health"
                element={
                  <PermissionRoute route="/health" screenName="Cluster Diagnostics">
                    <HealthPage />
                  </PermissionRoute>
                }
              />
              <Route path="*" element={<NotFoundPage />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
